import { forwardRef, useEffect, useState } from 'react'
import type { FocusEvent } from 'react'
import {
  formatPercentageInput,
  percentageInputToDecimalString,
  percentageInputToFixed,
  sanitizePercentageInput,
} from '@shared/lib/format/percent'
import { TextField } from '../Input/TextField'
import type { TextFieldProps } from '../Input/TextField'

export type PercentageFieldProps = Omit<
  TextFieldProps,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'inputMode'
> & {
  value: string | number | null | undefined
  min?: number
  max?: number
  fractionDigits?: number
  commitFractionDigits?: number
  onValueChange: (value: string) => void
  onValueCommit?: (value: string) => void
}

export const PercentageField = forwardRef<HTMLInputElement, PercentageFieldProps>(
  function PercentageField(
    {
      value,
      min = 0,
      max = 100,
      fractionDigits = 3,
      commitFractionDigits = 3,
      onValueChange,
      onValueCommit,
      onBlur,
      onFocus,
      rightSlot,
      ...props
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false)
    const [draft, setDraft] = useState(formatPercentageInput(value, fractionDigits))

    useEffect(() => {
      if (!focused) {
        setDraft(formatPercentageInput(value, fractionDigits))
      }
    }, [focused, fractionDigits, value])

    function handleFocus(event: FocusEvent<HTMLInputElement>) {
      setFocused(true)
      event.currentTarget.select()
      onFocus?.(event)
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
      setFocused(false)
      const committed = percentageInputToFixed(draft, min, max, commitFractionDigits)
      setDraft(formatPercentageInput(committed, fractionDigits))

      if (onValueCommit) {
        onValueCommit(committed)
      } else {
        onValueChange(committed)
      }

      onBlur?.(event)
    }

    return (
      <TextField
        {...props}
        ref={ref}
        inputMode="decimal"
        value={draft}
        rightSlot={rightSlot ?? <span>%</span>}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(event) => {
          const next = sanitizePercentageInput(event.target.value, fractionDigits)
          setDraft(next)
          onValueChange(percentageInputToDecimalString(next, fractionDigits))
        }}
      />
    )
  },
)
